import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Receipt, FileText, Camera, DollarSign, Check, Smartphone, ChevronRight, Zap, Wrench, Flame } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const ElectriciansLanding = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Free Invoice App for Electricians & Contractors | FinScan</title>
        <meta name="description" content="Create professional invoices and track job expenses in minutes. Free invoice generator built for electricians, plumbers, HVAC techs and home service contractors." />
        <link rel="canonical" href="https://finscan.store/electricians" />
        <meta property="og:title" content="Free Invoice App for Electricians & Contractors | FinScan" />
        <meta property="og:description" content="Create professional invoices and track job expenses in minutes. Free for electricians, plumbers, HVAC techs." />
        <meta property="og:url" content="https://finscan.store/electricians" />
      </Helmet>

      <div className="min-h-screen bg-[#030712]">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/40 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center space-x-2">
                <Receipt className="w-8 h-8 text-indigo-400" />
                <span className="text-2xl font-bold font-['Outfit'] bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400">
                  FinScan
                </span>
              </Link>
              <div className="flex items-center space-x-4">
                <Link to="/blog" className="text-muted-foreground hover:text-white transition-colors hidden sm:block">
                  Blog
                </Link>
                <button
                  onClick={() => navigate('/auth')}
                  className="bg-white text-black hover:bg-white/90 font-medium px-4 py-2 rounded-full transition-all"
                >
                  Sign In
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-yellow-500/10 via-transparent to-transparent" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center justify-center gap-4 mb-6">
                <Zap className="w-10 h-10 text-yellow-400" />
                <Wrench className="w-10 h-10 text-blue-400" />
                <Flame className="w-10 h-10 text-orange-400" />
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold font-['Outfit'] tracking-tight mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-yellow-200 to-yellow-400">
                  Invoicing Built for Electricians
                </span>
                <br />
                <span className="text-white/80 text-3xl md:text-5xl">& Home Service Pros</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed mb-8">
                Stop wasting time on paperwork. Create professional invoices, scan receipts, and track job expenses in under 2 minutes — <span className="text-white font-semibold">completely free</span>.
              </p>

              <button
                onClick={() => navigate('/auth')}
                data-testid="electricians-cta-hero"
                className="bg-yellow-500 text-black hover:bg-yellow-400 font-semibold px-8 py-4 rounded-full shadow-[0_0_30px_rgba(234,179,8,0.4)] transition-all transform hover:scale-105 active:scale-95 text-lg"
              >
                Start Free — No Credit Card Needed
              </button>
            </motion.div>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="py-20 bg-gradient-to-b from-transparent to-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="p-8 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-xl"
              >
                <div className="text-4xl mb-4">📋</div>
                <h3 className="text-xl font-semibold mb-3 text-yellow-400">Still writing invoices by hand?</h3>
                <p className="text-muted-foreground">
                  Generate professional invoices in 30 seconds. Add your logo, itemize labor and materials, and send directly to clients.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="p-8 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-xl"
              >
                <div className="text-4xl mb-4">📸</div>
                <h3 className="text-xl font-semibold mb-3 text-yellow-400">Drowning in paper receipts?</h3>
                <p className="text-muted-foreground">
                  Snap a photo of any receipt — our AI reads it instantly. Every supply run, tool purchase, and fuel cost tracked automatically.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="p-8 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-xl"
              >
                <div className="text-4xl mb-4">💰</div>
                <h3 className="text-xl font-semibold mb-3 text-yellow-400">Stressed about tax season?</h3>
                <p className="text-muted-foreground">
                  All your job expenses organized automatically. Export a clean report for your accountant in one click.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Features List */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold font-['Outfit'] text-center mb-12">
              Everything You Need to Run Your Business
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                'Create invoices in 30 seconds',
                'AI receipt scanner (snap & done)',
                'Track materials, labor, mileage',
                'Expense reports for taxes',
                'Works on phone, tablet, desktop',
                'Free forever plan available'
              ].map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.1 }}
                  className="flex items-center gap-3 p-4 bg-white/5 rounded-xl"
                >
                  <Check className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <span className="text-lg">{feature}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonial */}
        <section className="py-20 bg-gradient-to-b from-white/5 to-transparent">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="p-8 bg-gradient-to-br from-yellow-500/10 via-transparent to-transparent border border-yellow-500/20 rounded-2xl">
              <p className="text-2xl md:text-3xl font-light italic mb-6 text-white/90">
                "I used to spend Sunday nights doing invoices. Now it takes me 2 minutes after each job."
              </p>
              <p className="text-yellow-400 font-semibold">
                — Mike T., Licensed Electrician, Illinois
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold font-['Outfit'] text-center mb-12">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-6">
              <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
                <h3 className="text-lg font-semibold mb-2 text-yellow-400">Is FinScan really free for electricians?</h3>
                <p className="text-muted-foreground">
                  Yes. Our free plan covers unlimited invoices and receipt scanning for individuals. Upgrade to Pro for team features and advanced reports.
                </p>
              </div>
              
              <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
                <h3 className="text-lg font-semibold mb-2 text-yellow-400">Can I track materials and labor separately?</h3>
                <p className="text-muted-foreground">
                  Absolutely. Add line items for parts, labor hours, service calls — whatever fits your workflow.
                </p>
              </div>
              
              <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
                <h3 className="text-lg font-semibold mb-2 text-yellow-400">Does it work on my phone at the job site?</h3>
                <p className="text-muted-foreground">
                  Yes, FinScan works on any device. Scan receipts right from your truck.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-6">
              Join thousands of home service pros who ditched the paperwork
            </h2>
            <button
              onClick={() => navigate('/auth')}
              data-testid="electricians-cta-final"
              className="bg-yellow-500 text-black hover:bg-yellow-400 font-semibold px-10 py-5 rounded-full shadow-[0_0_30px_rgba(234,179,8,0.4)] transition-all transform hover:scale-105 text-xl"
            >
              Create Your Free Account
            </button>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-muted-foreground text-sm">
                © 2026 FinScan. Built for hardworking contractors.
              </p>
              <div className="flex gap-6 text-sm">
                <Link to="/1099" className="text-muted-foreground hover:text-white transition-colors">1099 Workers</Link>
                <Link to="/freelancers" className="text-muted-foreground hover:text-white transition-colors">Freelancers</Link>
                <Link to="/blog" className="text-muted-foreground hover:text-white transition-colors">Blog</Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ElectriciansLanding;
